import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeaders } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Check, X, Clock } from 'lucide-react';

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    if (user && user.role === 'admin') {
      fetchAllSubmissions();
    }
  }, [user, authLoading, navigate]);

  const fetchAllSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`, {
        headers: getAuthHeaders()
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (submissionId, status) => {
    try {
      await axios.patch(
        `${API}/admin/submissions/${submissionId}/status?status=${status}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Submission ${status}`);
      fetchAllSubmissions();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  if (authLoading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-screen py-32 bg-[#1A1412] flex items-center justify-center">
        <div className="text-[#A89B95]">Loading...</div>
      </div>
    );
  }

  const filteredSubmissions = submissions.filter(sub => {
    if (filter === 'all') return true;
    return sub.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    }
  };

  return (
    <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#FDFBF7] mb-4 tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-[#A89B95] text-lg font-['Outfit']">
            Manage submissions and content
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A89B95] text-sm">Total</p>
                  <p className="text-[#FDFBF7] text-3xl font-bold">{submissions.length}</p>
                </div>
                <Clock className="text-[#D97736]" size={32} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A89B95] text-sm">Pending</p>
                  <p className="text-[#FDFBF7] text-3xl font-bold">
                    {submissions.filter(s => s.status === 'pending').length}
                  </p>
                </div>
                <Clock className="text-yellow-400" size={32} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A89B95] text-sm">Approved</p>
                  <p className="text-[#FDFBF7] text-3xl font-bold">
                    {submissions.filter(s => s.status === 'approved').length}
                  </p>
                </div>
                <Check className="text-green-400" size={32} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A89B95] text-sm">Rejected</p>
                  <p className="text-[#FDFBF7] text-3xl font-bold">
                    {submissions.filter(s => s.status === 'rejected').length}
                  </p>
                </div>
                <X className="text-red-400" size={32} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <Tabs value={filter} onValueChange={setFilter} className="mb-8">
          <TabsList className="bg-[#2C221F]">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-[#D97736] data-[state=active]:text-white"
              data-testid="filter-all"
            >
              All ({submissions.length})
            </TabsTrigger>
            <TabsTrigger 
              value="pending" 
              className="data-[state=active]:bg-[#D97736] data-[state=active]:text-white"
              data-testid="filter-pending"
            >
              Pending ({submissions.filter(s => s.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger 
              value="approved" 
              className="data-[state=active]:bg-[#D97736] data-[state=active]:text-white"
              data-testid="filter-approved"
            >
              Approved ({submissions.filter(s => s.status === 'approved').length})
            </TabsTrigger>
            <TabsTrigger 
              value="rejected" 
              className="data-[state=active]:bg-[#D97736] data-[state=active]:text-white"
              data-testid="filter-rejected"
            >
              Rejected ({submissions.filter(s => s.status === 'rejected').length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Submissions List */}
        {loading ? (
          <div className="text-center text-[#A89B95] py-12" data-testid="loading-submissions">
            Loading submissions...
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="py-12 text-center">
              <p className="text-[#A89B95]" data-testid="no-submissions">
                No submissions found.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredSubmissions.map((submission, index) => (
              <Card 
                key={submission.id} 
                className="bg-[#2C221F] border-[#3D302C]"
                data-testid={`submission-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Submission Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7] mb-1">
                            {submission.full_name}
                          </h3>
                          <p className="text-[#A89B95] text-sm mb-2">{submission.email}</p>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-[#D97736]/20 text-[#D97736] border-[#D97736]/30 capitalize">
                              {submission.submission_type}
                            </Badge>
                            <Badge className={getStatusColor(submission.status)} data-testid={`status-badge-${index}`}>
                              {submission.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      {submission.bio_text && (
                        <div className="mb-4">
                          <p className="text-[#A89B95] text-sm">{submission.bio_text}</p>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-2 text-sm">
                        {submission.photos?.length > 0 && (
                          <span className="text-[#CFB53B]">{submission.photos.length} photo(s)</span>
                        )}
                        {submission.music_samples?.length > 0 && (
                          <span className="text-[#CFB53B]">{submission.music_samples.length} sample(s)</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex lg:flex-col gap-2">
                      <Button
                        onClick={() => updateStatus(submission.id, 'approved')}
                        disabled={submission.status === 'approved'}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-button-${index}`}
                      >
                        <Check size={16} className="mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => updateStatus(submission.id, 'rejected')}
                        disabled={submission.status === 'rejected'}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid={`reject-button-${index}`}
                      >
                        <X size={16} className="mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}